import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  MenuItem,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import api from '../services/api';

const Managers = () => {
  const [managers, setManagers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    branch: '',
  });

  useEffect(() => {
    fetchManagers();
    fetchBranches();
  }, []);

  const fetchManagers = async () => {
    try {
      const response = await api.get('/managers');
      setManagers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar encargados:', error);
      setError('Error al cargar encargados');
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
      // console.log('Sucursales cargadas:', response.data.length);
    } catch (error) {
      console.error('Error al cargar sucursales:', error);
      setError('Error al cargar sucursales. Por favor recarga la página.');
    }
  };

  const handleOpenDialog = (manager = null) => {
    if (manager) {
      setSelectedManager(manager);
      setFormData({
        name: manager.name,
        email: manager.email,
        password: '',
        branch: manager.branch?._id || '',
      });
    } else {
      setSelectedManager(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        branch: '',
      });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedManager(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      branch: '',
    });
    setError('');
  };

  const handleInputChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    // Validar campos
    if (!formData.name || !formData.email || !formData.branch) {
      setError('Por favor completa todos los campos requeridos');
      return;
    }

    if (!selectedManager && !formData.password) {
      setError('La contraseña es requerida para nuevos encargados');
      return;
    }

    try {
      const dataToSend = { ...formData };

      // No enviar password vacío si es edición
      if (selectedManager && !formData.password) {
        delete dataToSend.password;
      }

      if (selectedManager) {
        await api.put(`/managers/${selectedManager._id}`, dataToSend);
        setSuccess('Encargado actualizado exitosamente');
      } else {
        await api.post('/managers', dataToSend);
        setSuccess('Encargado creado exitosamente');
      }

      handleCloseDialog();
      fetchManagers();

      // Limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al guardar encargado:', error);
      setError(error.response?.data?.message || 'Error al guardar encargado');
    }
  };

  const handleOpenDeleteDialog = manager => {
    setSelectedManager(manager);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedManager(null);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/managers/${selectedManager._id}`);
      setSuccess('Encargado eliminado exitosamente');
      handleCloseDeleteDialog();
      fetchManagers();

      // Limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al eliminar encargado:', error);
      setError(error.response?.data?.message || 'Error al eliminar encargado');
      handleCloseDeleteDialog();
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h4" component="h1">
            Gestión de Encargados
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nuevo Encargado
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Sucursal</TableCell>
                <TableCell>Fecha de Creación</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {managers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 3 }}
                    >
                      No hay encargados registrados
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                managers.map(manager => (
                  <TableRow key={manager._id} hover>
                    <TableCell>{manager.name}</TableCell>
                    <TableCell>{manager.email}</TableCell>
                    <TableCell>
                      {manager.branch ? (
                        <Chip
                          icon={<BusinessIcon />}
                          label={manager.branch.name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Sin asignar
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(manager.createdAt).toLocaleDateString('es-ES')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(manager)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(manager)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Dialog para crear/editar encargado */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedManager ? 'Editar Encargado' : 'Nuevo Encargado'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {branches.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No hay sucursales creadas. Por favor crea al menos una sucursal
              antes de agregar encargados.
            </Alert>
          )}
          <TextField
            fullWidth
            label="Nombre"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={
              selectedManager
                ? 'Contraseña (dejar vacío para no cambiar)'
                : 'Contraseña'
            }
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            margin="normal"
            required={!selectedManager}
            helperText={
              selectedManager
                ? 'Solo completa si deseas cambiar la contraseña'
                : ''
            }
          />
          <TextField
            fullWidth
            select
            label="Sucursal"
            name="branch"
            value={formData.branch}
            onChange={handleInputChange}
            margin="normal"
            required
            helperText={
              branches.length === 0
                ? 'No hay sucursales creadas. Por favor crea una sucursal primero.'
                : 'Asigna una sucursal al encargado (puedes tener varios encargados por sucursal)'
            }
            disabled={branches.length === 0}
          >
            {branches.length === 0 ? (
              <MenuItem value="">No hay sucursales disponibles</MenuItem>
            ) : (
              branches.map(branch => (
                <MenuItem key={branch._id} value={branch._id}>
                  {branch.name} - {branch.city || 'Sin ciudad'}
                </MenuItem>
              ))
            )}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={branches.length === 0}
          >
            {selectedManager ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar al encargado{' '}
            <strong>{selectedManager?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Managers;
